import {
    clearAsideFilterValues,
    clearPickerFilterValues,
    setModalPickerShowClear,
    setModalPickerShowClearCount,
    toggleFilterAside,
} from 'actions/ui';
import { SystemText } from '@salesperformanceint/e4shared';
import Filter from './components/filter';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import './filterMenu.css';
import { apiGet } from 'services';
import { setDashboardCollection } from 'actions/dashboard';

import posthog from 'posthog-js';

export const FilterMenu = ({ inline, picker }) => {
    const dispatch = useDispatch();

    const [loading, setLoading] = React.useState(true);
    const filterMenu = React.useRef(null);
    const closeButton = React.useRef(null);
    const filterFields = useSelector(state => (picker ? state.ui.pickerFilters : state.ui.asideFilters)) || [];

    const { user } = useSelector(state => state.user);

    const {
        areas,
        businessUnits,
        coaches,
        competencies,
        courses,
        departments,
        languages,
        locations,
        pathways,
        roles,
        teams,
    } = useSelector(state => state.dashboard);

    const setFieldData = property => {
        switch (property) {
            case 'area_Ids':
                return areas;
            case 'business_Unit_Ids':
                return businessUnits;
            case 'coach_Ids':
                return coaches;
            case 'competency_Ids':
                return competencies;
            case 'department_Ids':
                return departments;
            case 'language_Ids':
                return languages;
            case 'location_Ids':
                return locations;
            case 'pathway_Ids':
                return pathways;
            case 'role_Ids':
                return roles;
            case 'manager_Ids':
            case 'owner_Ids':
                return teams;
            default:
                return [{ id: 0, name: `Field ${property} not found in setFieldData` }];
        }
    };

    React.useEffect(() => {
        const keyUp = e => {
            if (e.key === 'Escape') dispatch(toggleFilterAside(null));
        };

        document.addEventListener('keydown', keyUp, false);

        return () => {
            document.removeEventListener('keydown', keyUp, false);
        };
    }, []);

    React.useEffect(() => {
        const promises = [];

        if (!roles?.length) {
            promises.push(apiGet('role', user.guid, response => dispatch(setDashboardCollection('roles', response))));
        }

        if (!departments?.length) {
            promises.push(
                apiGet('department', user.guid, response => dispatch(setDashboardCollection('departments', response)))
            );
        }

        if (!locations?.length) {
            promises.push(
                apiGet('location', user.guid, response => dispatch(setDashboardCollection('locations', response)))
            );
        }

        if (!businessUnits?.length) {
            promises.push(
                apiGet('business-unit', user.guid, response => dispatch(setDashboardCollection('units', response)))
            );
        }

        if (!languages?.length) {
            promises.push(
                apiGet('language', user.guid, response => dispatch(setDashboardCollection('languages', response)))
            );
        }

        if (!pathways?.length) {
            promises.push(
                apiGet('pathway-list', user.guid, response => dispatch(setDashboardCollection('pathways', response)))
            );
        }

        if (!teams?.length) {
            promises.push(
                apiGet('dashboard-teams', user.guid, response => dispatch(setDashboardCollection('teams', response)))
            );
        }

        if (!coaches?.length) {
            promises.push(
                apiGet('dashboard-coaches', user.guid, response => dispatch(setDashboardCollection('coaches', response)))
            );
        }

        if (!areas?.length) {
            promises.push(
                apiGet('competency-area', user.guid, response => dispatch(setDashboardCollection('areas', response)))
            );
        }

        if (!courses?.length) {
            promises.push(
                apiGet('all-courses', user.guid, response => dispatch(setDashboardCollection('courses', response)))
            );
        }

        if (!competencies?.length) {
            promises.push(
                apiGet('profile-downline-competencies', user.guid, response =>
                    dispatch(setDashboardCollection('competencies', response))
                )
            );
        }

        if (!promises.length) {
            setLoading(false);
        } else {
            Promise.all(promises).then(() => {
                setLoading(false);
            });
        }
    }, []);

    React.useEffect(() => {
        setTimeout(() => {
            closeButton.current?.focus();
        }, 500);
    }, [closeButton.current]);

    React.useEffect(() => {
        const fieldCount = totalFilters;
        dispatch(setModalPickerShowClear(fieldCount > 0));
        dispatch(setModalPickerShowClearCount(fieldCount));
    }, [filterFields]);

    const totalFilters = React.useMemo(() => {
        return filterFields.reduce((a, c) => {
            if (c.type === 'list') {
                return a + (c.value || []).length;
            }

            if (c.type === 'courseStatus' || c.type === 'behaviourScore') {
                return a + (c.value.filter(x => x.id) || []).length;
            }

            return a + (c.value ? 1 : 0);
        }, 0);
    }, [filterFields]);

    const clearAllFilters = () => {
        if (picker) {
            dispatch(clearPickerFilterValues());
        } else {
            dispatch(clearAsideFilterValues());
        }
    };

    const showResults = () => {
        const filters = filterFields
            .filter(f => {
                if (!f.value || (Array.isArray(f.value) && f.value.length === 0)) return false;
                if (typeof f.value === 'string' && (f.value.trim() === '' || f.value.trim() === '|')) return false;
                return true;
            })
            .map(f => f.property)
            .join(',');

        posthog.capture('tracker', { userEvent: 'show-filter-results', activeFilters: filters });
        dispatch(toggleFilterAside(false));
    };

    React.useEffect(() => {
        !inline && posthog.capture('tracker', { userEvent: 'open-filter' });
    }, []);

    return (
        <div className={inline ? 'filter-aside inline' : 'p30 filter-aside'} ref={filterMenu} data-testid="filter-menu">
            {!inline && (
                <div className="top flex flex-center mb20">
                    <div className="flex-grow">
                        {totalFilters > 0 && (
                            <button className="btn btn-link" onClick={clearAllFilters}>
                                <span className="mr10">{SystemText('General/Controls/Filters/Clear')}</span>
                                {totalFilters > 0 && <label className="label label-danger">{totalFilters}</label>}
                            </button>
                        )}
                    </div>
                    <button
                        ref={closeButton}
                        data-testid="button-explore-close"
                        className="btn btn-link pull-right"
                        aria-label={SystemText('General/Controls/Filters/Close')}
                        onClick={() => dispatch(toggleFilterAside(false))}
                    >
                        <i className="fa fa-times-circle" />
                    </button>
                </div>
            )}
            <div className="filters middle flex-grow">
                {filterFields?.map(f => {
                    if (f.type === 'list') {
                        f.data = f.data?.length ? f.data : setFieldData(f.property);
                    }

                    return (
                        <Filter key={`filter-${f.property}`} field={f} loading={loading} inline={inline} picker={picker} />
                    );
                })}
            </div>
            {!inline && (
                <div className="bottom">
                    <button className="btn btn-primary pull-right mt10 mb30" onClick={showResults}>
                        {SystemText('General/Controls/Filters/Results')}
                    </button>
                </div>
            )}
        </div>
    );
};

export default FilterMenu;
